/* eslint-disable max-classes-per-file */

export enum SurveyQuestionType {
    intro,
    statement,
    options,
}

export class SurveyGroupValues {
    // current state
    public static readonly CurrentStateNonAlignment = 1;
    public static readonly CurrentStateGrowingPains = 2;
    public static readonly CurrentStateLackOfClarity = 3;
    public static readonly CurrentStateOverwhelm = 4;
    public static readonly CurrentStateStuck = 5;

    // future state
    public static readonly SurveyFutureMaximisation = 6;
    public static readonly SurveyFutureIndependence = 7;
    public static readonly SurveyFutureFreedom = 8;
    public static readonly SurveyFutureExcellence = 9;
    public static readonly SurveyFutureVision = 10;

    public static GroupMapping(value: number) {
        switch (value) {
            case SurveyGroupValues.CurrentStateNonAlignment: return "non-alignment";
            case SurveyGroupValues.CurrentStateGrowingPains: return "growing-pains";
            case SurveyGroupValues.CurrentStateLackOfClarity: return "lack-of-clarity";
            case SurveyGroupValues.CurrentStateOverwhelm: return "overwhelm";
            case SurveyGroupValues.CurrentStateStuck: return "stuck";

            case SurveyGroupValues.SurveyFutureMaximisation: return "maximisation";
            case SurveyGroupValues.SurveyFutureIndependence: return "independence";
            case SurveyGroupValues.SurveyFutureFreedom: return "freedom";
            case SurveyGroupValues.SurveyFutureExcellence: return "excellence";
            case SurveyGroupValues.SurveyFutureVision: return "impact";

            default:
                throw new Error();
        }
    }
}

export class SurveyQuestionOption {
    public option!: string;
    public group!: number;
}

export class SurveyQuestion {
    public question!: string;
    public options: SurveyQuestionOption[] = [];

    public subQuestion?: string;
    public type: SurveyQuestionType = SurveyQuestionType.options;
}

export class Survey {
    public questions: SurveyQuestion[] = [];

    public get rankedWants() {
        const wants = new Map<number, number>();

        for (const question of this.questions) {
            for (const option of question.options) {
                const existingCount = wants.get(option.group) ?? 0;
                wants.set(option.group, existingCount + question.options.indexOf(option));
            }
        }

        const sortedArray = Array.from(wants.entries())
            .sort((a, b) => {
                return a[1] - b[1];
            });

        return sortedArray;
    }

    public isCurrentState(group: number) {
        return group == SurveyGroupValues.CurrentStateNonAlignment
            || group == SurveyGroupValues.CurrentStateGrowingPains
            || group == SurveyGroupValues.CurrentStateLackOfClarity
            || group == SurveyGroupValues.CurrentStateOverwhelm
            || group == SurveyGroupValues.CurrentStateStuck;
    }

    public get primaryCurrentState() {
        const want = this.rankedWants.filter((i) => this.isCurrentState(i[0]))[0];
        return [SurveyGroupValues.GroupMapping(want[0]), want[1]];
    }

    public get secondaryCurrentState() {
        const want = this.rankedWants.filter((i) => this.isCurrentState(i[0]))[1];
        return [SurveyGroupValues.GroupMapping(want[0]), want[1]];
    }

    public get primaryFutureState() {
        const want = this.rankedWants.filter((i) => !this.isCurrentState(i[0]))[0];
        return [SurveyGroupValues.GroupMapping(want[0]), want[1]];
    }

    public get secondaryFutureState() {
        const want = this.rankedWants.filter((i) => !this.isCurrentState(i[0]))[1];
        return [SurveyGroupValues.GroupMapping(want[0]), want[1]];
    }

    public randomiseOptions() {
        for (const question of this.questions) {
            question.options.sort(() => Math.random() - 0.5);
        }
    }

    public questionIndex(question: SurveyQuestion) {
        return this.questions.indexOf(question);
    }
}

